import { normalizeRoomCode } from './codes.js';

/**
 * The paths of the website that open the apps when a link to them is tapped
 * on a phone that has one (Android App Links, iOS Universal Links): meeting
 * links, and the page of a recording shared by email. The Android manifest's
 * link filter and the server's apple-app-site-association list the same.
 */
export const APP_LINK_PATHS = ['/m/', '/recordings/'] as const;

export type AppLinkRoute =
  | { kind: 'meeting'; code: string; path: string }
  | { kind: 'recording'; id: string; path: string }
  /** A meeting link whose code is not a code: the app goes home and says so. */
  | { kind: 'invalid'; path: '/' };

const RECORDING_ID = /^\/recordings\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/?$/i;

/**
 * Where in the app a link that opened it leads, or null when it is not one
 * of the app's links. Of a meeting link's query only the organiser's host
 * token (?host=) is kept: it makes the person opening it the host, as on the
 * website. Anything else in the address is dropped.
 */
export function routeForAppLink(url: string): AppLinkRoute | null {
  // Taken apart by hand: this package runs where the URL class is not declared.
  const parts = /^https?:\/\/[^/?#\s]+(\/[^?#\s]*)?(\?[^#\s]*)?(#\S*)?$/i.exec(url.trim());
  if (!parts) return null;
  const pathname = parts[1] ?? '/';
  const meeting = /^\/m\/([^/]+)\/?$/.exec(pathname);
  if (meeting) {
    const raw = decode(meeting[1]!);
    const code = raw === null ? null : normalizeRoomCode(raw);
    if (!code) return { kind: 'invalid', path: '/' };
    const host = queryParam(parts[2] ?? '', 'host');
    return { kind: 'meeting', code, path: host ? `/m/${code}?host=${encodeURIComponent(host)}` : `/m/${code}` };
  }
  const recording = RECORDING_ID.exec(pathname);
  if (recording) {
    const id = recording[1]!.toLowerCase();
    return { kind: 'recording', id, path: `/recordings/${id}` };
  }
  return null;
}

/** A percent-encoded part of an address, or null when it is not validly encoded. */
function decode(value: string): string | null {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}

/** One parameter of a query string, read the way browsers read forms ("+" is a space). */
function queryParam(query: string, name: string): string | null {
  for (const pair of query.replace(/^\?/, '').split('&')) {
    const eq = pair.indexOf('=');
    const key = decode((eq < 0 ? pair : pair.slice(0, eq)).replace(/\+/g, ' '));
    if (key === name) return decode((eq < 0 ? '' : pair.slice(eq + 1)).replace(/\+/g, ' '));
  }
  return null;
}
